import { CLOSE_TO_SEARCH, MY_DRAWN_AREA, MY_TRAVEL_TIME_AREA } from '@propertypal/shared/src/constants/search';
import useRecentSearches, { RecentSearch } from '@propertypal/shared/src/hooks/useRecentSearches';
import useSearchState from '@propertypal/shared/src/hooks/useSearchState';
import { propertySearch, propertyUrlSearch, selectProperties } from '@propertypal/shared/src/reducers/properties';
import { initialState, selectSearch, setSearch } from '@propertypal/shared/src/reducers/search';
import { RootState, useAppDispatch } from '@propertypal/shared/src/reducers/store';
import { trackEnquiryClick, trackGaEvent, trackSearch } from '@propertypal/shared/src/services/analytics';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { Property } from '@propertypal/shared/src/types';
import { WebsiteUser } from '@propertypal/shared/src/types/websiteUser';
import hasFeature from '@propertypal/shared/src/utils/featureFlag';
import platform from '@propertypal/shared/src/utils/platform';
import { propertyNewsDetailsUrl, propertyNewsDevelopmentUrl } from '@propertypal/shared/src/utils/regex';
import getTotalProperties from '@propertypal/shared/src/utils/search/getTotalProperties';
import { CLOSE_TO_QUERY_PARAM } from '@propertypal/web-ui/src/close-search/useCloseToSearch';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import useMediaQuery from '@propertypal/web-ui/src/media-query/useMediaQuery';
import Breadcrumbs from '@propertypal/web-ui/src/search/Breadcrumbs';
import PageNumbers from '@propertypal/web-ui/src/search/PageNumbers';
import { Text } from '@propertypal/web-ui/src/typography';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { GetServerSideProps, GetServerSidePropsContext } from 'next/types';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTheme } from 'styled-components';
import AdBox from '../components/ads/AdBox';
import generateMetaTags from '../components/layout/MetaTags';
import EnquiryModal from '../components/property/EnquiryModal';
import PropertyList from '../components/search/PropertyList';
import SearchBar from '../components/search/SearchBar/SearchBar';
import SearchTitle from '../components/search/SearchTitle';
import IMAGE_LOCATOR, { IMAGE_LOCATOR_LARGE, IMAGE_LOCATOR_SMALL } from '../constants/imageLocator';
import useGpt from '../hooks/useGptSlot';
import { setNeighboursData } from '../hooks/useNeighbours';
import usePropertyFavourite from '../hooks/usePropertyFavourite';
import usePropertyHide from '../hooks/usePropertyHide';
import { setTargeting } from '../services/ads/googletag';
import cachedRequest from '../services/cachedRequest';
import { getHeatingParam, getSearchPriceParam } from '../utils/ad-utils';
import { requireAuthToken } from '../utils/auth';
import stitchRequestUrl from '../utils/stitchRequestUrl';
import useCallAgent from '../utils/useCallAgent';

const SearchPreviewModal = dynamic(() => import('../components/search/SearchPreviewModal'), { ssr: false });
const SaveSearchModal = dynamic(() => import('../components/search/SaveSearchModal'), { ssr: false });
const EditSearchModal = dynamic(() => import('../components/search/EditSearchModal'), { ssr: false });

const AD_TOP_SIZES = [[1, 1], 'fluid'];
const AD_TOP_MOBILE_SIZES = [[1, 1], 'fluid'];
const AD_WIDE_MIDDLE_SIZES = [[728, 90], [970, 250], [970, 90], 'fluid'];
const AD_MIDDLE_SIZES = [[728, 90], 'fluid'];
const AD_MIDDLE_MOBILE_SIZES = [[300, 250], [336, 280], 'fluid'];
const AD_WIDE_BOTTOM_SIZES = [[728, 90], [970, 90], 'fluid'];
const AD_BOTTOM_SIZES = [[728, 90], 'fluid'];
const AD_BOTTOM_MOBILE_SIZES = [[320, 100], [300, 250], [336, 280], 'fluid'];
const AD_MOBILE_STYLES = {
  marginTop: 10,
  marginBottom: 10,
  paddingTop: 25,
  paddingBottom: 25,
};
const AD_MIDDLE_DESKTOP_STYLES = {
  paddingTop: 35,
  paddingBottom: 35,
};

const AD_STICKY_FOOTER_SIZES = [[320, 50], 'fluid'];
const AD_STICKY_FOOTER_STYLES = platform.select(
  { bottom: 70, left: 0, margin: 0, padding: 10 },
  { bottom: 0, left: 0, margin: 0, padding: 10 },
);

const getSiteMapLinks = async (ctx?: GetServerSidePropsContext) => {
  if (ctx?.resolvedUrl?.includes('northern-ireland') || ctx?.query?.args?.includes('northern-ireland')) {
    return cachedRequest(`/site-map/footer/nir?q=${ctx.query.preset}`, ctx, 6);
  }

  if (ctx?.resolvedUrl?.includes('republic-of-ireland') || ctx?.query?.args?.includes('republic-of-ireland')) {
    return cachedRequest(`/site-map/footer/irl?q=${ctx.query.preset}`, ctx, 6);
  }

  return null;
};

interface Props {
  loggedIn: boolean;
  photoScroller: boolean;
  loggedInUser?: WebsiteUser;
}

const getPnRedirect = async (url: string, ctx: any) => {
  try {
    const result = await axios(
      {
        method: 'GET',
        url: `${API_URL}/seo-redirect?pnPath=${encodeURIComponent(url)}`,
      },
      undefined,
      ctx,
    );

    return result.data.path;
  } catch {
    return '';
  }
};

export const getServerSideProps: GetServerSideProps = requireAuthToken(async (ctx, store) => {
  const [url, excludedParams] = stitchRequestUrl(ctx.query, [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'google_debug',
    'google_console',
    'ogy_log',
    'ogy_test_mode',
    'ogy_in_article_type',
    'ogy_creative_ratio',
    'ogy_creative_orientation',
    'ogy_test_bid_price',
    'google_preview',
    'iu',
    'lineItemId',
    'creativeId',
    'gdfp_req',
    CLOSE_TO_QUERY_PARAM,
  ]);

  const detailsRegex = new RegExp(propertyNewsDetailsUrl);
  const developmentRegex = new RegExp(propertyNewsDevelopmentUrl);

  if (detailsRegex.test(url) || developmentRegex.test(url)) {
    const redirectUrl = await getPnRedirect(url, ctx);

    if (redirectUrl) {
      return {
        redirect: {
          destination: redirectUrl,
          permanent: true,
        },
      };
    }

    return {
      notFound: true,
    };
  }

  const result = await store.dispatch(propertyUrlSearch(IMAGE_LOCATOR, url, ctx));
  const siteMapLinks = await getSiteMapLinks(ctx);

  const { properties } = store.getState();

  if (properties.error) {
    throw properties.error;
  }

  const mUrl = url.replace(/%60/g, '');
  const pUrl = properties.data?.url.replace(/%60/g, '');

  if (properties.data && properties.data.url && pUrl !== mUrl) {
    const paramPrefix = properties.data.url.includes('?') ? '&' : '?';
    ctx.res.setHeader('Cache-control', 'no-cache');

    return {
      redirect: {
        destination: `${properties.data.url}${excludedParams && `${paramPrefix}${excludedParams}`}`,
        permanent: true,
      },
      props: {
        siteMapLinks,
      },
    };
  }

  ctx.res.statusCode = properties.data.results.length > 0 ? 200 : 404;

  return {
    props: {
      siteMapLinks,
      photoScroller: hasFeature(result.headers, 'sc-33944-search-results-photo-scroller'),
      loggedInUser: result.data.loggedInUser || null,
    },
  };
});

const Search: FunctionComponent<Props> = (props) => {
  const properties = useSelector(selectProperties);
  const search = useSelector(selectSearch);
  const searchState = useSearchState();
  const theme = useTheme();
  const dispatch = useDispatch();
  const asyncDispatch = useAppDispatch();
  const store = useStore<RootState>();
  const router = useRouter();
  const { modal, showPhoneModal } = useCallAgent();
  const isLandscapeMobile = useMediaQuery({ query: `(max-height: 500px)` });
  const [emailProperty, setEmailProperty] = useState<Property>();
  const [saveSearchOpen, setSaveSearchOpen] = useState<boolean>(false);
  const [editSearchOpen, setEditSearchOpen] = useState<boolean>(false);
  const [searchModalOpen, setSearchModalOpen] = useState<boolean>(false);
  const [showSearchPreview, setShowSearchPreview] = useState('');
  const { recentSearches, addRecentSearch, removeRecentSearch } = useRecentSearches(properties.data?.description);
  const title = properties.data ? `${properties.data.description.complete} - ${platform.title}` : platform.title;
  const filters = { ...searchState.appFilters, ...searchState.nlpFilters };
  const showTotalProperties = !['COUNTRY', 'REGION'].includes(properties.data?.locationDetail?.termType || 'COUNTRY');
  const [propertyType, formattedTotal] = getTotalProperties(
    filters.category,
    properties.data?.totalUnits,
    properties.data?.totalDevelopments,
  );
  const onFavourite = usePropertyFavourite(props.loggedIn);
  const onHideToggle = usePropertyHide(props.loggedIn);
  const hideWideAds = useMediaQuery({ query: `(1340px <= width <= 1360px) or (width < 1030px)` });
  const middleAdSizes = hideWideAds ? AD_MIDDLE_SIZES : AD_WIDE_MIDDLE_SIZES;
  const bottomAdSizes = hideWideAds ? AD_BOTTOM_SIZES : AD_WIDE_BOTTOM_SIZES;

  const handleSaveSearchClick = () => {
    if (!props.loggedIn) {
      router.push(`/login?sourcePath=${encodeURIComponent(`${router.asPath}#saveSearch`)}`);
    } else if (properties.data) {
      if (properties.data.savedSearch) {
        setEditSearchOpen(true);
      } else {
        setSaveSearchOpen(true);
      }
    }
  };

  const handleSubmit = async () => {
    trackSearch('SRP', searchState.searchText, filters);
    await asyncDispatch(propertySearch(IMAGE_LOCATOR));

    const state = store.getState();

    if (state.properties.data?.url && state.properties.data?.url !== window.location.pathname) {
      await router.push(state.properties.data?.url, undefined, {
        shallow: true,
      });
    }
  };

  const handleSortChange = async (value: any) => {
    searchState.updatePristineText();

    const newFilters = {
      ...searchState.appFilters,
      sortOrder: value,
    };

    searchState.setAppFilters(newFilters);

    dispatch(
      setSearch({
        text: searchState.searchText,
        filters: newFilters,
      }),
    );
    await asyncDispatch(propertySearch(IMAGE_LOCATOR_LARGE));

    const mProperties = store.getState().properties;

    if (mProperties.data?.url && mProperties.data?.url !== window.location.pathname) {
      router.push(mProperties.data?.url, undefined, {
        shallow: true,
      });
    }
  };

  const handleAlternativeSearch = (term: string) => {
    dispatch(
      setSearch({
        text: term,
        filters: { ...initialState.filters },
      }),
    );

    handleSubmit();
  };

  const handlePreviewSubmit = (polyIds: number[]) => {
    dispatch(
      setSearch({
        text: showSearchPreview === 'drawSearch' ? MY_DRAWN_AREA : MY_TRAVEL_TIME_AREA,
        filters: {
          ...searchState.appFilters,
          polyIds,
        },
      }),
    );
    handleSubmit();

    setSearchModalOpen(false);
    setShowSearchPreview('');
  };

  const handleRecentSearch = (recentSearch: RecentSearch) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    trackGaEvent('recent_search_click', { location: 'Side Bar' });

    dispatch(
      setSearch({
        text: recentSearch.text,
        filters: { ...initialState.filters, ...recentSearch.filters },
      }),
    );
    handleSubmit();
  };

  const onCloseToSearch = async (polyIds: number[]) => {
    dispatch(
      setSearch({
        text: CLOSE_TO_SEARCH,
        filters: {
          ...searchState.appFilters,
          polyIds,
          intersection: true,
        },
      }),
    );

    await handleSubmit();

    return true;
  };

  const handlePropertyClick = (property: Property) => {
    if (properties.data) {
      setNeighboursData(properties.data, property);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (properties.data) {
        addRecentSearch({
          description: properties.data.description,
          text: searchState.searchText,
          filters,
        });
      }
    });
  }, [properties.data?.description.stem]);

  useEffect(() => {
    if (window.location.hash === '#saveSearch' && props.loggedIn && properties.data) {
      window.location.hash = '';
      handleSaveSearchClick();
    }
  }, [props.loggedIn, properties.data]);

  useEffect(() => {
    // re-fetch properties if history back is used on a shallow route
    if (
      !properties.loading &&
      ((properties.data && properties.data.url !== router.asPath) || (!properties.data && !properties.error))
    ) {
      asyncDispatch(propertyUrlSearch(IMAGE_LOCATOR, router.asPath));
    }
  }, [router.asPath]);

  useGpt(() => {
    setTargeting({
      Category: properties.data?.gptTargeting?.Category,
      SaleType: properties.data?.gptTargeting?.SaleType,
      Location: properties.data?.gptTargeting?.Location,
      Price: getSearchPriceParam(properties.data),
      HeatingType: getHeatingParam(properties.data),
      LoggedIn: props.loggedIn ? 'yes' : 'no',
    });
  }, [properties.data?.gptTargeting]);

  const searchTitle = (
    <SearchTitle
      handleSortChange={handleSortChange}
      searchState={searchState}
      onSaveSearchClick={handleSaveSearchClick}
      onAlternativeSearch={handleAlternativeSearch}
      searchUrl={properties.data?.url}
    />
  );

  const upperMiddleAds = (
    <>
      <AdBox
        id="SRPtop_desktop"
        config="SRPtop_desktop"
        sizes={middleAdSizes}
        declaration
        containerStyle={AD_MIDDLE_DESKTOP_STYLES}
        hideUnder={768}
        url={router.asPath}
        refresh
        timedRefresh
      />

      <AdBox
        id="SRPtop_mobile"
        config="SRPtop_mobile"
        sizes={AD_MIDDLE_MOBILE_SIZES}
        hideOver={768}
        url={router.asPath}
        containerStyle={AD_MOBILE_STYLES}
        declaration
        refresh
        timedRefresh
      />
    </>
  );

  const lowerMiddleAds = (
    <>
      {properties.data && properties.data.results.length >= 8 && (
        <>
          <AdBox
            id="SRPmiddle_desktop"
            config="SRPmiddle_desktop"
            sizes={middleAdSizes}
            containerStyle={AD_MIDDLE_DESKTOP_STYLES}
            declaration
            hideUnder={768}
            url={router.asPath}
            refresh
            timedRefresh
          />

          <AdBox
            id="SRPmiddle_mobile"
            config="SRPmiddle_mobile"
            sizes={AD_MIDDLE_MOBILE_SIZES}
            hideOver={768}
            url={router.asPath}
            containerStyle={AD_MOBILE_STYLES}
            declaration
            refresh
            timedRefresh
          />
        </>
      )}
    </>
  );

  const footerAds = (
    <>
      {properties.data && properties.data.results.length > 9 && (
        <>
          <AdBox
            id="SRPbottom_desktop"
            config="SRPbottom_desktop"
            sizes={bottomAdSizes}
            hideUnder={768}
            containerStyle={AD_MIDDLE_DESKTOP_STYLES}
            declaration
            url={router.asPath}
            refresh
            timedRefresh
          />

          <AdBox
            id="SRPbottom_mobile"
            config="SRPbottom_mobile"
            sizes={AD_BOTTOM_MOBILE_SIZES}
            hideOver={768}
            url={router.asPath}
            containerStyle={AD_MOBILE_STYLES}
            declaration
            refresh
            timedRefresh
          />
        </>
      )}
    </>
  );

  return (
    <>
      <Head>
        {generateMetaTags({
          title,
          description: properties.data?.description.metaDescription,
          url: properties.data?.url || '/search',
        })}
      </Head>

      <SearchBar
        searchModalOpen={searchModalOpen}
        onSearchModalToggle={setSearchModalOpen}
        imageLocator={IMAGE_LOCATOR}
        loading={properties.loading}
        searchState={searchState}
        onSubmit={handleSubmit}
        onSaveSearchClick={handleSaveSearchClick}
        onDrawSearchClick={() => setShowSearchPreview('drawSearch')}
        onTravelTimeClick={() => setShowSearchPreview('travelTime')}
        recentSearches={recentSearches}
        removeRecentSearch={removeRecentSearch}
        loggedIn={props.loggedIn}
        onCloseToSearch={onCloseToSearch}
        importantPlaces={properties.data?.importantPlaces}
      />

      {showSearchPreview && (
        <SearchPreviewModal
          show={showSearchPreview}
          onClose={() => setShowSearchPreview('')}
          onSubmit={handlePreviewSubmit}
          initialPolyIds={search.filters.polyIds}
        />
      )}

      <PageWrapper
        containerStyle={{
          backgroundColor: platform.select(theme.backgroundLightest, 'transparent'),
          borderTop: platform.select(`1px solid ${theme.keyline}`, 'none'),
          paddingBottom: 30,
        }}
        overflow="visible"
      >
        <Breadcrumbs crumbs={properties.data?.breadcrumbs} disableBack padding={0} />

        {(!properties.data || (properties.data.results && properties.data.results.length > 0)) && (
          <>
            <AdBox
              id="SRPsponsor_desktop"
              config="SRPsponsor_desktop"
              sizes={AD_TOP_SIZES}
              hideUnder={980}
              renderedStyle={{ marginBottom: 30 }}
              url={router.asPath}
              refresh={false}
              collapse
            />
            <AdBox
              id="SRPsponsor_mobile"
              config="SRPsponsor_mobile"
              hideOver={979}
              sizes={AD_TOP_MOBILE_SIZES}
              renderedStyle={{ margin: '15px 0' }}
              url={router.asPath}
              refresh={false}
              collapse
            />
          </>
        )}

        <PropertyList
          currency={filters.currency}
          onFavourite={onFavourite}
          onHideToggle={onHideToggle}
          imageLocator={IMAGE_LOCATOR_LARGE}
          thumbImageLocator={IMAGE_LOCATOR_SMALL}
          locationDetail={properties.data?.locationDetail}
          // onSaveSearchClick={handleSaveSearchClick}
          onDrawSearchClick={() => setShowSearchPreview('drawSearch')}
          onTravelTimeClick={() => setShowSearchPreview('travelTime')}
          onEmailClick={(property) => {
            trackEnquiryClick('SRP', 'Property Box', property);
            setEmailProperty(property);
          }}
          onCallClick={(property) => showPhoneModal(property, undefined, undefined, 'SRP', 'Property Box')}
          // @ts-ignore
          onCallAgentClick={(agent) => showPhoneModal(undefined, undefined, agent, 'SRP', 'Property Box')}
          // handleSortChange={handleSortChange}
          upperMiddleAd={upperMiddleAds}
          lowerMiddleAd={lowerMiddleAds}
          footer={footerAds}
          recentSearches={recentSearches}
          onRecentSearchClick={handleRecentSearch}
          onRemoveRecentSearch={removeRecentSearch}
          title={searchTitle}
          photoScroller={props.photoScroller}
          handleAlternativeSearch={handleAlternativeSearch}
          handleSaveSearchClick={handleSaveSearchClick}
          onPropertyClick={handlePropertyClick}
        />

        {properties.data && properties.data.results.length > 0 && properties.data.totalPages && (
          <>
            {showTotalProperties && formattedTotal !== '0' && (
              <Text align="center" mb={60} style={{ padding: '0 30px' }}>
                <b style={{ color: theme.primary }}>{formattedTotal}</b> {propertyType} found for{' '}
                <b style={{ color: theme.primary }}>{properties.data?.description.stem}</b>
              </Text>
            )}

            {properties.data.totalPages > 1 && (
              <PageNumbers
                currentUrl={properties.data.url}
                currentPage={properties.data.page + 1}
                totalPages={properties.data.totalPages}
              />
            )}
          </>
        )}
      </PageWrapper>

      {!isLandscapeMobile && (
        <div id="sticky-footer-ad" className="hide-with-keyboard">
          <AdBox
            hideOver={1024}
            config="SRPstickyfooter"
            id="SRPstickyfooter"
            sizes={AD_STICKY_FOOTER_SIZES}
            containerStyle={AD_STICKY_FOOTER_STYLES}
            renderedStyle={{ background: 'white', borderTop: '1px solid #cccccc' }}
            position="fixed"
            url={router.asPath}
            refresh
            timedRefresh
          />
        </div>
      )}

      {saveSearchOpen && <SaveSearchModal show={saveSearchOpen} onClose={() => setSaveSearchOpen(false)} />}

      {editSearchOpen && properties.data && properties.data.savedSearch && (
        <EditSearchModal
          savedSearch={properties.data.savedSearch}
          show={editSearchOpen}
          onClose={() => setEditSearchOpen(false)}
        />
      )}

      {modal}

      {emailProperty && (
        <EnquiryModal
          show={!!emailProperty}
          onClose={() => setEmailProperty(undefined)}
          property={emailProperty}
          loggedIn={props.loggedIn}
          loggedInUser={props.loggedInUser}
          page="SRP"
        />
      )}
    </>
  );
};

export default Search;
